import React, { useEffect } from 'react';
import mermaid from 'mermaid';

/**
 * This component works exactly as same as Mermaid.tsx, but uses the
 * new version of Mermaid library with updated API.
 * */
export const Mermaid2: React.FC<{ text: string }> = ({ text }) => {
  const ref = React.useRef<HTMLDivElement | null>(null);

  // Initialize Mermaid with the new API
  useEffect(() => {
    /**
     * This initialization is done only once when the component mounts.
     * This is to ensure that Mermaid is set up correctly
     * and ready to render diagrams.
     * */
    mermaid.initialize({
      startOnLoad: true, // This ensures that Mermaid starts rendering diagrams on page load
      securityLevel: 'loose', // Allows for more flexible rendering, especially with dynamic content
      theme: 'default', // Use 'default' theme for better compatibility
      logLevel: 5, // Set log level to 5 for detailed logging
      flowchart: {
        nodeSpacing: 50, // Spacing between nodes in the flowchart
        rankSpacing: 50, // Spacing between ranks in the flowchart
      },
    });
  }, []);

  // Render the Mermaid diagram when the text changes
  useEffect(() => {
    /**
     * This function is used to render the Mermaid diagram asynchronously.
     * */
    const asyncRender = async () => {
      if (ref === null || ref.current === null || text === '') {
        return;
      }

      try {
        const { svg, bindFunctions } = await mermaid.render('preview', text, ref.current);
        ref.current.innerHTML = svg; // Set the inner HTML of the ref to the rendered SVG
        if (bindFunctions) {
          bindFunctions(ref.current); // Bind any functions if needed
        }
      } catch (error) {
        console.error('Error rendering Mermaid diagram:', error);
      }
    };

    asyncRender();
  }, [text, ref]);

  return <div ref={ref} className="mermaid" key="preview" />;
};
